import api from "@/app/api";
import { filterNonUniqueByProperty } from "@/app/apiutils";
import { containerService } from "@/app/services/extensions/containerServiceExtended";
import { useI18n } from "@/plugins/i18n";
import { computed } from "vue";
import type { ContainerHistory } from "@/app/services/generated/containerService";
import { ContainerFilter } from "@/app/services/generated/containerService";
import { makeDeepRef } from "@/components/composables/reactivityTypes";

export interface ScannedContainerItem {
  permLabel: string;
  scannedThisSession: boolean; // If the container belongs to the current session
  errorMessage: string | null;
  hasBeenValidated: boolean;
  mostRecentHistory?: ContainerHistory;
}

export interface ScannedContainersEvent {
  toAdd: ScannedContainerItem[];
  toRemove: string[];
  noteToAppend?: string;
}

export const useScanContainers = (opts?: {
  additionalContainerValidation?: (container: ScannedContainerItem) => string;
}) => {
  const scannedContainers = makeDeepRef<ScannedContainerItem[]>([]);
  const { $m, $t } = useI18n();
  const addContainer = async (label: string) => {
    const labelToAdd: ScannedContainerItem = {
      permLabel: label,
      scannedThisSession: true,
      errorMessage: null,
      hasBeenValidated: false,
    };
    await validateScanning(labelToAdd, true);
    scannedContainers.value.unshift(labelToAdd);
    scannedContainers.value = filterNonUniqueByProperty(
      scannedContainers.value,
      (x) => x.permLabel
    );
  };

  const removeContainer = (permLabel: string) => {
    scannedContainers.value = scannedContainers.value.filter(
      (x) => x.permLabel !== permLabel
    );
  };

  const validateScanning = async (
    containerItem?: ScannedContainerItem,
    checkLatestHistories?: boolean
  ) => {
    const toScanLabels = containerItem
      ? [containerItem]
      : scannedContainers.value.filter((x) => x.scannedThisSession);

    if (toScanLabels.length === 0) {
      return true;
    }

    toScanLabels.forEach((x) => {
      x.hasBeenValidated = false;
      x.errorMessage = null;
    });

    const containers = await containerService.getContainersByPermLabels(
      api.tenantId,
      toScanLabels.map((x) => x.permLabel)
    );

    const permLabelToLatestHistoryMap: Map<string, ContainerHistory> =
      new Map();

    if (checkLatestHistories) {
      const containerFilter = new ContainerFilter();
      containerFilter.containerIds = containers.map(
        (container) => container.id
      );

      const summaries = await containerService.getContainerSummaries(
        api.tenantId,
        containerFilter
      );

      summaries.containers.forEach(({ container, latestHistory }) => {
        permLabelToLatestHistoryMap.set(container.permLabel, latestHistory);
      });
    }

    // check all permLabels are valid
    toScanLabels.forEach((x) => {
      if (checkLatestHistories) {
        x.mostRecentHistory = permLabelToLatestHistoryMap.get(x.permLabel);
      }

      if (!containers.some((c) => c.permLabel === x.permLabel)) {
        x.errorMessage = $t($m.keys.containers_not_found).toString();
      } else if (opts?.additionalContainerValidation) {
        x.errorMessage = opts.additionalContainerValidation(x) || null;
      }
      x.hasBeenValidated = true;
    });

    return scanningErrorMessage.value === null;
  };

  const scanningErrorMessage = computed(() => {
    if (scannedContainers.value.length === 0) {
      return null;
    }
    if (scannedContainers.value.some((x) => x.errorMessage)) {
      return $t($m.keys.containers_fill_error).toString();
    }
    return null;
  });

  const currentSessionPermLabels = computed(() =>
    scannedContainers.value.filter((x) => x.scannedThisSession)
  );

  const scannedContainersText = computed(() =>
    $t($m.keys.containers_scanned_this_session_summary, {
      scannedThisSession: `${currentSessionPermLabels.value.length}`,
    }).toString()
  );

  const scannedContainersSummary = computed(() =>
    $t($m.keys.containers_total_filled_summary, {
      totalScanned: `${scannedContainers.value.length}`,
    }).toString()
  );

  const getSessionScannedContainers = () => {
    return scannedContainers.value.filter((x) => x.scannedThisSession);
  };

  const resetUseScanContainersState = () => {
    scannedContainers.value.forEach((container) => {
      container.scannedThisSession = false;
    });
  };

  return {
    addContainer,
    removeContainer,
    validateScanning,
    scannedContainers,
    scanningErrorMessage,
    scannedContainersText,
    scannedContainersSummary,
    currentSessionPermLabels,
    resetUseScanContainersState,
    getSessionScannedContainers,
  };
};
